import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../utils/constants";
import { decryptId } from "../utils/encrypt";
import { useDispatch, useSelector } from "react-redux";
import { addSingleMaterial, triggerRefresh } from "../utils/materialsSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Loader from "./Loader";
import Toast from "./Toast";
import { defaultMaterial } from "../utils/materialsSlice";

const MaterialDetail = ({ id, goBackToList }) => {
  const actualId = id ? decryptId(decodeURIComponent(id)) : null;
  const prevActualId = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setShowToastMessage] = useState("");
  const dispatch = useDispatch();

  const chemicalTypes = [
    "ORGANIC PIGMENT",
    "INORGANIC PIGMENT",
    "FILLER",
    "HARDNER",
    "EPOXY RESIN",
    "POLYSTER RESIN",
    "ADDITIVE",
  ];
  let material = useSelector((state) => state.material.selected);
  // console.log("material",material)

  const fetchMaterial = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/materialMaster/${actualId}`,
        {
          withCredentials: true,
        }
      );
      dispatch(addSingleMaterial(response.data.data));
      setLoading(false);
    } catch (err) {
      setLoading(false);

      console.error("Error fetching material:", err);
    }
  };

  useEffect(() => {
    // Only fetch if actualId changed
    if (actualId && actualId !== prevActualId.current) {
      fetchMaterial();
      prevActualId.current = actualId;
    } else if (!actualId) {
      dispatch(addSingleMaterial(defaultMaterial));
      prevActualId.current = null;
    }
  }, [actualId, defaultMaterial, dispatch]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      density: parseFloat(values.density),
      tax: values.tax ? parseFloat(values.tax) : "",
      basePrice: parseFloat(values.basePrice),
    };
    try {
      setLoading(true);
      if (actualId) {
        // Update existing material
        const response = await axios.patch(
          `${BASE_URL}/materialMaster/update-material/${actualId}`,
          payload,
          {
            withCredentials: true,
          }
        );
        setLoading(false);
        setShowToast(true);
        setShowToastMessage("Material is updated successfully");
        dispatch(addSingleMaterial(response.data.data));
        // dispatch(triggerRefresh());
      } else {
        // Create new material
        const response = await axios.post(
          `${BASE_URL}/materialMaster/add-material`,
          payload,
          {
            withCredentials: true,
          }
        );
        setShowToast(true);
        setShowToastMessage("Material is saved successfully");
        setLoading(false);

        dispatch(addSingleMaterial(defaultMaterial));
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setShowToast(true);
        dispatch(addSingleMaterial(payload));

        setShowToastMessage("Chemical name is duplicate");
      }
      setLoading(false);
    }
  };

  const goBackToListHandler = () => {
    dispatch(triggerRefresh());
    goBackToList();
  };

  const validationSchema = Yup.object().shape({
    chemicalName: Yup.string().required("Chemical Name is required"),
    chemicalCode: Yup.string().required("Chemical Code is required"),

    density: Yup.string()
    .nullable()
    .notRequired()
      .matches(
        /^\d{1,3}(\.\d{1,3})?$/,
        "Max 3 digits before and after decimal"
      ),

    basePrice: Yup.string()
      .required("Base Price is required")
      .matches(
        /^\d{1,7}(\.\d{1,2})?$/,
        "Max 7 digits before and 2 after decimal"
      ),

    tax: Yup.string()
      .nullable()
      .matches(/^\d{1,2}(\.\d{1,2})?$/, "Max 2 digits before and after decimal")
      .notRequired(),
  });

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size={12} text="Loading Application..." />
      </div>
    );

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold ">
        {actualId ? "Edit Material" : "Create Material"}
      </h1>
      <Toast
        message={toastMessage}
        visible={showToast}
        onClose={() => setShowToast(false)}
      />
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={material}
        onSubmit={handleSubmit}
        validateOnChange={true} // ✅ Trigger validation on every keypress
        validateOnBlur={true}
      >
        {({ values, handleChange }) => (
          <Form className="grid grid-cols-2 gap-4">
            {/* Chemical Name */}
            <div className="col-span-2 gap-2 flex justify-end">
              <button
                type="submit"
                className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 transition"
              >
                Save
              </button>
              <button
                onClick={goBackToListHandler}
                className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 transition"
              >
                Back
              </button>
            </div>
            <div className="flex flex-col">
              <label htmlFor="chemicalName" className="mb-1 font-medium">
                Chemical Name
              </label>
              <Field
                id="chemicalName"
                name="chemicalName"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Enter chemical name"
                onChange={handleChange}
                value={values.chemicalName}
              />
              <ErrorMessage
                name="chemicalName"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Chemical Code */}
            <div className="flex flex-col">
              <label htmlFor="chemicalCode" className="mb-1 font-medium">
                Chemical Code
              </label>
              <Field
                id="chemicalCode"
                name="chemicalCode"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Enter chemical code"
                onChange={(e) => {
                  const upperCased = e.target.value.toUpperCase();
                  // Trigger Formik's handleChange manually with transformed value
                  handleChange({
                    target: {
                      name: "chemicalCode",
                      value: upperCased,
                    },
                  });
                }}
                value={values.chemicalCode}
              />
              <ErrorMessage
                name="chemicalCode"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Density */}
            <div className="flex flex-col">
              <label htmlFor="density" className="mb-1 font-medium">
                Density
              </label>
              <Field
                name="density"
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="e.g. 123.456"
              />
              <ErrorMessage
                name="density"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Base Price */}
            <div className="flex flex-col">
              <label htmlFor="basePrice" className="mb-1 font-medium">
                Base Price (kg)
              </label>
              <Field
                name="basePrice"
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="e.g. 123.456"
              />
              <ErrorMessage
                name="basePrice"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Tax */}
            <div className="flex flex-col">
              <label htmlFor="tax" className="mb-1 font-medium">
                Tax (%)
              </label>
              <Field
                name="tax"
                type="text"
                inputMode="decimal"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="e.g. 123.456"
              />
              <ErrorMessage
                name="tax"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Chemical Type */}
            <div>
              <label
                htmlFor="chemicalType"
                className="block font-semibold text-gray-700 mb-1"
              >
                Chemical Type
              </label>
              <Field
                as="select"
                name="chemicalType"
                id="chemicalType"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white"
              >
                <option value="">Select chemical type</option>
                {chemicalTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="chemicalType"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Company Name */}
            <div className="flex flex-col">
              <label htmlFor="companyName" className="mb-1 font-medium">
                Company Name
              </label>
              <Field
                id="companyName"
                name="companyName"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.companyName}
              />
              <ErrorMessage
                name="companyName"
                component="div"
                className="text-red-500 text-sm"
              />
            </div>

            {/* Address */}
            <div className="flex flex-col">
              <label htmlFor="addressLine1" className="mb-1 font-medium">
                Address
              </label>
              <Field
                id="addressLine1"
                name="addressLine1"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.addressLine1}
              />
            </div>

            {/* Contact Name */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation1.name"
                className="mb-1 font-medium"
              >
                Contact Name 1
              </label>
              <Field
                id="contactInformation1.name"
                name="contactInformation1.name"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation1.name}
              />
            </div>

            {/* Contact Number */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation1.contactNumber"
                className="mb-1 font-medium"
              >
                Contact Number 1
              </label>
              <Field
                id="contactInformation1.contactNumber"
                name="contactInformation1.contactNumber"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation1.contactNumber}
              />
            </div>
            {/* Contact Name */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation2.name"
                className="mb-1 font-medium"
              >
                Contact Name 2
              </label>
              <Field
                id="contactInformation2.name"
                name="contactInformation2.name"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation2.name}
              />
            </div>

            {/* Contact Number */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation2.contactNumber"
                className="mb-1 font-medium"
              >
                Contact Number 2
              </label>
              <Field
                id="contactInformation2.contactNumber"
                name="contactInformation2.contactNumber"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation2.contactNumber}
              />
            </div>
            {/* Contact Name */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation3.name"
                className="mb-1 font-medium"
              >
                Contact Name 3
              </label>
              <Field
                id="contactInformation3.name"
                name="contactInformation3.name"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation3.name}
              />
            </div>

            {/* Contact Number */}
            <div className="flex flex-col">
              <label
                htmlFor="contactInformation3.contactNumber"
                className="mb-1 font-medium"
              >
                Contact Number 3
              </label>
              <Field
                id="contactInformation13.contactNumber"
                name="contactInformation3.contactNumber"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                onChange={handleChange}
                value={values.contactInformation3.contactNumber}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MaterialDetail;
